import { Button, Dialog } from '@today/ui-primitives'
import { type ReactElement, useRef } from 'react'
import { DateTimeInput, ErrorText, FocusTextArea, useFocusOnOpen } from './content-fields'
import type { Translate } from './content-types'

export interface ApproveValuesView {
	reason: string
	validFrom: string
	validTo: string
}

/**
 * Props for {@link ApproveDialogView}.
 *
 * @property values - Reason and the window as `datetime-local` values (read as UTC).
 * @property errors - Translated messages by field (`reason`, `validFrom`, `validTo`).
 * @property error - A server refusal shown at the top of the still-open dialog.
 */
export interface ApproveDialogViewProps {
	t: Translate
	isOpen: boolean
	values: ApproveValuesView
	errors: Record<string, string>
	error?: string | null
	isBusy?: boolean
	onChange: (field: keyof ApproveValuesView, value: string) => void
	onConfirm: () => void
	onCancel: () => void
}

/** Approve with a reason and the validity window (organism). Focus starts on the reason. */
export function ApproveDialogView(props: ApproveDialogViewProps): ReactElement {
	const { t, values, errors } = props
	const reasonRef = useRef<HTMLTextAreaElement>(null)
	useFocusOnOpen(reasonRef, props.isOpen)
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onCancel())}
			title={t('review.approveTitle')}
			labels={{ close: t('actions.close') }}
		>
			<div className="flex flex-col gap-3">
				<ErrorText message={props.error} />
				<FocusTextArea
					label={t('review.reason')}
					value={values.reason}
					error={errors.reason}
					inputRef={reasonRef}
					onChange={(v) => props.onChange('reason', v)}
				/>
				<DateTimeInput
					label={t('review.validFrom')}
					description={t('review.utcHint')}
					value={values.validFrom}
					error={errors.validFrom}
					onChange={(v) => props.onChange('validFrom', v)}
				/>
				<DateTimeInput
					label={t('review.validTo')}
					description={t('review.validToHint')}
					value={values.validTo}
					error={errors.validTo}
					onChange={(v) => props.onChange('validTo', v)}
				/>
				<div className="flex gap-2">
					<Button isDisabled={props.isBusy} onPress={props.onConfirm}>
						{t('review.approve')}
					</Button>
					<Button variant="muted" onPress={props.onCancel}>
						{t('actions.cancel')}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
